import type { Alpha3Code } from 'i18n-iso-countries'
import { useTranslation } from 'react-i18next'

import { formatAmount, formatDate, getPaymentStatusBadge, type PaymentStatus } from '../../utils/utils'
import styles from '../CandidatesContainer.module.scss'
import { useCandidates } from '../candidatesContext/useCandidatesContext'
import { SearchAndFilters } from '../searchAndFilters/SearchAndFilters'

import { getCountryNameFromAlpha3Code } from '@percent/partner-dashboard/i18n/countries'
import {
  Badge,
  FlagAvatar,
  Flex,
  Loader,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip
} from '@percent/lemonade'
import type { TableColumn } from '@percent/lemonade'

export function DisbursementsTable() {
  const { t, i18n } = useTranslation()

  const { candidates, sort, navigateToCandidate, queryParams } = useCandidates()
  const isSearchActive = !!queryParams.searchTerm && queryParams.searchTerm.trim() !== ''

  const columnsHeaders: TableColumn[] = [
    { id: 'country', content: t('table.header.country'), props: { width: '10%' } },
    { id: 'organization', content: t('table.header.organization'), props: { width: '25%' } },
    { id: 'amountAwarded', content: t('table.header.amountAwarded'), props: { width: '15%' } },
    { id: 'amountPaid', content: t('table.header.amountPaid'), props: { width: '15%' } },
    { id: 'paymentStatus', content: t('table.header.paymentStatus'), props: { width: '15%' } },
    { id: 'paidAt', content: t('table.header.paymentDate'), isSortable: true, props: { width: '20%' } }
  ]

  if (candidates.isLoading) {
    return <Loader />
  }

  const emptyTableTitle = isSearchActive
    ? t('grants.searchEmptyTable.title')
    : t('grants.candidatesInStateTable.emptyTable.title')
  const emptyTableDescription = isSearchActive ? '' : t('grants.candidatesInStateTable.emptyTable.description')
  const illustration = isSearchActive ? 'no-results' : 'no-grant-candidates'

  return (
    <TableProvider
      columns={columnsHeaders}
      totalResults={candidates.totalResults}
      isLoading={candidates.isLoading}
      handleRequestSort={sort.handleRequestSort}
      sortDirection={sort.direction}
      sortByColumn="paidAt"
      illustration={illustration}
      emptyTableTitle={emptyTableTitle}
      emptyTableDescription={emptyTableDescription}
      className={styles.tableWrapper}
      dataTestId="disbursements-table"
    >
      {(candidates.totalResults > 0 || queryParams.searchTerm) && (
        <Flex direction="column" data-testid="search-and-filters">
          <SearchAndFilters enabledFilters={{ organisationSearch: true, currentState: false }} />
        </Flex>
      )}
      <Table>
        <TableBody>
          {candidates.data?.map(({ id, organisation, award, paymentDetails }) => {
            const { label: paymentStatusLabel, variant: paymentStatusVariant } = getPaymentStatusBadge(
              paymentDetails?.status as PaymentStatus,
              t
            )

            return (
              <TableRow
                key={`candidate-${id}`}
                data-testid={`candidate-${id}-row`}
                onClick={() => navigateToCandidate(id)}
                className={styles.tableRow}
              >
                <TableCell inlineStyle={{ width: '150px' }} dataTestId="country-code-cell">
                  <Flex align="center">
                    <Tooltip
                      content={getCountryNameFromAlpha3Code(organisation.countryCode as Alpha3Code)}
                      placement="right"
                    >
                      <div className={styles.tooltipContent}>
                        <FlagAvatar code3={organisation.countryCode as Alpha3Code} width="28px" />
                      </div>
                    </Tooltip>
                  </Flex>
                </TableCell>
                <TableCell dataTestId="organisation-name-cell">{organisation.name}</TableCell>
                <TableCell dataTestId="amount-awarded-cell">
                  {award
                    ? formatAmount({
                        amount: award?.amount,
                        currency: award?.currency,
                        locale: i18n.language,
                        t
                      })
                    : '-'}
                </TableCell>
                <TableCell dataTestId="amount-paid-cell">
                  {paymentDetails?.payment
                    ? formatAmount({
                        amount: paymentDetails.payment.amount,
                        currency: paymentDetails.payment.currency,
                        locale: i18n.language,
                        t
                      })
                    : '-'}
                </TableCell>
                <TableCell dataTestId="payment-status-cell">
                  <Badge variant={paymentStatusVariant}>{paymentStatusLabel}</Badge>
                </TableCell>
                <TableCell>{paymentDetails?.paidAt ? formatDate(paymentDetails.paidAt) : '-'}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Flex direction="column" p="10px">
        <TablePagination
          totalResultsText={t('table.results', {
            count: candidates.totalResults ?? undefined
          })}
          previousPage={candidates.previousPage}
          nextPage={candidates.nextPage}
        />
      </Flex>
    </TableProvider>
  )
}
